import React from "react";
import {connect} from "react-redux";
import {Modal, NumberInput, TextInput} from "../../../../components";
import NumberUtils from "../../../../utils/NumberUtil";
import SelectInput from "../../../../components/select-input/SelectInput";
import RemotingService from "../../../../services/remoting-service/RemotingService";
import ActionMenu from "../../../../components/action-menu/ActionMenu";
import EditIcon from "../../../../components/action-menu/icons/EditIcon";
import ActionMenuItem from "../../../../components/action-menu/ActionMenuItem";
import DeleteIcon from "../../../../components/action-menu/icons/DeleteIcon";
import ConfirmIcon from "../../../../components/action-menu/icons/ConfirmIcon";
import CancelIcon from "../../../../components/action-menu/icons/CancelIcon";
import EnumAppointmentType from '../../../appointment/enums/EnumAppointmentType';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dropdown} from "primereact/dropdown";
import {FormField} from "../../../../components/form";
import {Button} from "react-bootstrap";
import confirmAndDeleteDialog from "../../../../components/dialog/ConfirmAndDeleteDialog";
import NotificationService from "../../../../services/notification-service/NotificationService";
import MoneyUtil from "../../../../utils/MoneyUtil";
import AuthService from "../../../../services/auth-service/AuthService";

class AddPackagePanel extends React.Component {

    state = {
        packages: [],
        clinics: [],
        selectedClinicId: null,
        selectedPackages: [],
        selectedPackageIdForEdit: null,
        updatePackageDto: {},
        isAddingNew: false,
        newPackageData: {
            name: '',
            description: '',
            type: '',
            sessionCount: '',
            discountPercentage: '',
            pricePerSession: '',
            firstSessionPrice: '',
            validityInDays: '',
            taxPercentage: 0
        }
    };

    componentDidMount() {
        this.retrievePackages();
        this.retrieveClinics();
    }

    retrievePackages = () => {
        RemotingService.getRemoteCall('api/packages', null, (response) => {
            this.setState({packages: response});
        });
    }

    retrieveClinics = () => {
        RemotingService.getRemoteCall('api/clinic/list', null, (result) => {
            const clinics = result.items.map(c => ({label: c.name, id: c.id, country: c.country, currency: c.currency}));
            this.setState({clinics, selectedClinicId: clinics.length === 1 ? clinics[0].id : null});
        });
    }

    getSelectedClinic = () => {
        return this.state.clinics.find(c => c.id === this.state.selectedClinicId);
    }

    getFormattingCountry = () => {
        const clinic = this.getSelectedClinic();
        if (clinic) {
            return clinic.country;
        }

        const {uaeSelected} = AuthService.getSelectedClinicCountriesInfo();
        return uaeSelected ? "UAE" : "KUWAIT";
    }

    // Get type options - only new patient and follow up
    getTypeOptions = () => {
        return [
            {
                label: 'New Patient',
                value: 'NEW_PATIENT'
            },
            {
                label: 'Follow Up',
                value: 'FOLLOW_UP'
            }
        ];
    }

    // Start editing a package
    editPackage = (packageData) => {
        this.setState({
            selectedPackageIdForEdit: packageData.id,
            updatePackageDto: {...packageData},
            isAddingNew: false
        });
    }

    // Save package changes
    savePackageChanges = (packageData) => {
        const {updatePackageDto} = this.state;

        // Basic validation
        if (!updatePackageDto.name || !updatePackageDto.description || !updatePackageDto.type || !updatePackageDto.sessionCount ||
            !updatePackageDto.pricePerSession || !updatePackageDto.firstSessionPrice) {
            NotificationService.showNotification({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fill all required fields'
            });
            return;
        }

        RemotingService.putRemoteCall(`api/packages/${packageData.id}`, updatePackageDto, (response) => {
            this.setState({selectedPackageIdForEdit: null, updatePackageDto: {}});
            this.retrievePackages();
            NotificationService.showNotification({
                severity: 'success',
                summary: 'Success',
                detail: 'Package updated successfully'
            });
        });
    }

    // Discard package changes
    discardPackageChanges = () => {
        this.setState({selectedPackageIdForEdit: null, updatePackageDto: {}, isAddingNew: false});
        this.retrievePackages();
    }

    // Delete package
    handlePackageDelete = (packageData) => {
        confirmAndDeleteDialog(
            "Delete Package",
            `Are you sure you want to delete the package "${EnumAppointmentType[packageData.type]?.name || packageData.type}"?`,
            `api/packages/${packageData.id}`,
            () => { this.retrievePackages(); },
            "Delete",
            "Cancel"
        );
    }

    // Add new package row
    addNewPackageRow = () => {
        const newPackage = {
            id: 'new',
            name: '',
            description: '',
            type: 'NEW_PATIENT',
            sessionCount: 1,
            discountPercentage: 0,
            pricePerSession: 0,
            firstSessionPrice: 0,
            validityInDays: 30,
            taxPercentage: 0,
            taxAmount: 0,
            amount: 0
        };

        this.setState({
            packages: [newPackage, ...this.state.packages],
            selectedPackageIdForEdit: 'new',
            updatePackageDto: {...newPackage},
            isAddingNew: true
        });
    }

    // Save new package
    saveNewPackage = () => {
        const {updatePackageDto} = this.state;

        // Basic validation
        if (!updatePackageDto.name || !updatePackageDto.description || !updatePackageDto.type || !updatePackageDto.sessionCount ||
            !updatePackageDto.pricePerSession || !updatePackageDto.firstSessionPrice) {
            NotificationService.showNotification({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fill all required fields'
            });
            return;
        }

        const {id, ...packageDataWithoutId} = updatePackageDto;

        RemotingService.postRemoteCall('api/packages', packageDataWithoutId, (response) => {
            this.setState({
                selectedPackageIdForEdit: null,
                updatePackageDto: {},
                isAddingNew: false
            });
            this.retrievePackages();
            NotificationService.showNotification({
                severity: 'success',
                summary: 'Success',
                detail: 'Package created successfully'
            });
        });
    }

    // Cancel new package
    cancelNewPackage = () => {
        this.setState({
            packages: this.state.packages.filter(p => p.id !== 'new'),
            selectedPackageIdForEdit: null,
            updatePackageDto: {},
            isAddingNew: false
        });
    }

    // Handle field changes for editing
    onPackageFieldChange = (packageData, fieldName, value) => {
        // Use current updatePackageDto state as base instead of packageData to preserve changes
        const currentPackage = this.state.updatePackageDto;
        const updatedPackage = {...currentPackage, [fieldName]: value};

        // Format decimals for money fields (exclude integer fields)
        if (['pricePerSession', 'firstSessionPrice', 'amount', 'discountPercentage', 'taxPercentage'].includes(fieldName)) {
            updatedPackage[fieldName] = value === undefined || value === null ? "" : value;
            if (updatedPackage[fieldName] > 1 && updatedPackage[fieldName] % 1 !== 0) {
                updatedPackage[fieldName] = this.formatDecimalsForCountry(value, this.getFormattingCountry());
            }
        }

        // Handle type-specific logic
        if (fieldName === 'type') {
            if (value === 'FOLLOW_UP') {
                // For FOLLOW_UP, set firstSessionPrice to same as pricePerSession
                updatedPackage.firstSessionPrice = updatedPackage.pricePerSession;
            }
        }

        // For FOLLOW_UP type, sync firstSessionPrice with pricePerSession
        if (fieldName === 'pricePerSession' && updatedPackage.type === 'FOLLOW_UP') {
            updatedPackage.firstSessionPrice = value;
        }

        // Recalculate amount and tax when relevant fields change
        if (fieldName === 'sessionCount' || fieldName === 'pricePerSession' || fieldName === 'firstSessionPrice' || fieldName === 'taxPercentage') {
            updatedPackage.amount = this.calculateAmount(updatedPackage);
            updatedPackage.taxAmount = this.calculateTaxAmount(updatedPackage);
        }

        this.setState({updatePackageDto: updatedPackage});
    }

    formatDecimalsForCountry = (value, country) => {
        const maxDecimalLengthForCountry = MoneyUtil.getFractionDigitsWithCountry(country);
        return NumberUtils.toFixedMaxLength(value, maxDecimalLengthForCountry);
    }

    // Calculate amount based on session count and prices
    calculateAmount = (packageData) => {
        const sessions = parseInt(packageData.sessionCount) || 0;
        const pricePerSess = parseFloat(packageData.pricePerSession) || 0;
        const firstSessPrice = parseFloat(packageData.firstSessionPrice) || 0;

        if (sessions <= 0) return 0;
        if (sessions === 1) return firstSessPrice;

        return firstSessPrice + (pricePerSess * (sessions - 1));
    }

    // Calculate tax amount extracted from tax-inclusive amount
    calculateTaxAmount = (packageData) => {
        const amount = this.calculateAmount(packageData);
        const taxPercentage = parseFloat(packageData.taxPercentage) || 0;

        if (taxPercentage <= 0) return 0;

        return amount * (taxPercentage / (100 + taxPercentage));
    }

    // Template for editable text input
    textInputTemplate = (packageData, fieldName) => {
        return packageData.id === this.state.selectedPackageIdForEdit ?
            <FormField>
                <TextInput
                    value={this.state.updatePackageDto[fieldName] || ''}
                    style={{width: '100%'}}
                    onChange={(value) => this.onPackageFieldChange(packageData, fieldName, value)}
                />
            </FormField>
            :
            packageData[fieldName] || '-';
    }

    // Template for editable number input (with decimals)
    numberInputTemplate = (packageData, fieldName) => {
        const isFirstSessionPrice = fieldName === 'firstSessionPrice';
        const isFollowUpType = this.state.updatePackageDto.type === 'FOLLOW_UP' || packageData.type === 'FOLLOW_UP';
        const isDisabled = isFirstSessionPrice && isFollowUpType && packageData.id === this.state.selectedPackageIdForEdit;

        // Get country-based decimal precision (UAE: 2, Kuwait: 3)
        const country = this.getFormattingCountry();

        return packageData.id === this.state.selectedPackageIdForEdit ?
            <FormField min={0} validateOn={this.state.updatePackageDto[fieldName]}>
                <NumberInput
                    value={this.state.updatePackageDto[fieldName] || ''}
                    style={{width: '100%'}}
                    disabled={isDisabled}
                    allowDecimal={true}
                    maxFractionDigits={MoneyUtil.getFractionDigitsWithCountry(country)}
                    onChange={(value) => this.onPackageFieldChange(packageData, fieldName, value)}
                />
            </FormField>
            :
            (packageData[fieldName] !== undefined && packageData[fieldName] !== null) ?
                Number(packageData[fieldName]).toFixed(MoneyUtil.getFractionDigitsWithCountry(country)) : '-';
    }

    // Template for integer input (no decimals)
    integerInputTemplate = (packageData, fieldName) => {
        return packageData.id === this.state.selectedPackageIdForEdit ?
            <FormField min={1} validateOn={this.state.updatePackageDto[fieldName]}>
                <NumberInput
                    value={this.state.updatePackageDto[fieldName] || 0}
                    style={{width: '100%'}}
                    min={1}
                    step={1}
                    onChange={(value) => this.onPackageFieldChange(packageData, fieldName, value)}
                />
            </FormField>
            :
            (packageData[fieldName] !== undefined && packageData[fieldName] !== null) ?
                packageData[fieldName] : '-';
    }

    // Template for type column with SelectInput
    typeTemplate = (packageData) => {
        return packageData.id === this.state.selectedPackageIdForEdit ?
            <FormField>
                <SelectInput
                    value={this.state.updatePackageDto.type || ''}
                    options={this.getTypeOptions()}
                    displayProperty="label"
                    valueProperty="value"
                    placeholder="Select Type"
                    style={{width: '100%'}}
                    onChange={(value) => this.onPackageFieldChange(packageData, 'type', value)}
                />
            </FormField>
            :
            EnumAppointmentType[packageData.type]?.name || packageData.type || '-';
    }

    // Template for calculated amount (read-only)
    amountTemplate = (packageData) => {
        // Get country-based decimal precision (UAE: 2, Kuwait: 3)
        const country = this.getFormattingCountry();
        const fractionDigits = MoneyUtil.getFractionDigitsWithCountry(country);
        const defaultValue = country === "UAE" ? '0.00' : '0.000';

        if (packageData.id === this.state.selectedPackageIdForEdit) {
            return this.state.updatePackageDto.amount?.toFixed(fractionDigits) || defaultValue;
        }
        return packageData.amount?.toFixed(fractionDigits) || defaultValue;
    }

    // Template for calculated tax amount
    taxAmountTemplate = (packageData) => {
        const fractionDigits = 2;
        const defaultValue = '0.00';

        let taxAmount;
        if (packageData.id === this.state.selectedPackageIdForEdit) {
            taxAmount = this.calculateTaxAmount(this.state.updatePackageDto);
        } else {
            taxAmount = packageData.taxAmount || 0;
        }

        return taxAmount.toFixed(fractionDigits) || defaultValue;
    }

    // Action menu template
    actionTemplate = (packageData) => {
        const isEditing = packageData.id === this.state.selectedPackageIdForEdit;
        const isNewPackage = this.state.isAddingNew && packageData.id === 'new';

        let menuItems = [];

        if (!isEditing) {
            menuItems = [
                <ActionMenuItem
                    key="edit"
                    icon={<EditIcon/>}
                    onClick={() => this.editPackage(packageData)}
                    text={"Edit"}
                />,
                <ActionMenuItem
                    key="delete"
                    icon={<DeleteIcon/>}
                    onClick={() => this.handlePackageDelete(packageData)}
                    text={"Delete"}
                />
            ];
        } else if (isNewPackage) {
            menuItems = [
                <ActionMenuItem
                    key="save-new"
                    icon={<ConfirmIcon/>}
                    onClick={() => this.saveNewPackage()}
                    text={"Save"}
                />,
                <ActionMenuItem
                    key="cancel-new"
                    icon={<CancelIcon/>}
                    onClick={() => this.cancelNewPackage()}
                    text={"Cancel"}
                />
            ];
        } else {
            menuItems = [
                <ActionMenuItem
                    key="save"
                    icon={<ConfirmIcon/>}
                    onClick={() => this.savePackageChanges(packageData)}
                    text={"Save"}
                />,
                <ActionMenuItem
                    key="cancel"
                    icon={<CancelIcon/>}
                    onClick={() => this.discardPackageChanges()}
                    text={"Cancel"}
                />
            ];
        }

        return (
            <ActionMenu id={`packageActionMenu_${packageData.id}`}>
                {menuItems}
            </ActionMenu>
        );
    }

    // Handle package selection for adding to patient
    handleCheckboxChange = (id) => {
        this.setState(prevState => {
            const selectedPackageIds = prevState.selectedPackageIds.includes(id)
                ? prevState.selectedPackageIds.filter(packageId => packageId !== id)
                : [...prevState.selectedPackageIds, id];
            return {selectedPackageIds};
        });
    }

    // Add selected packages to patient
    handlePackageAdd = () => {
        const {patientId, closeModal} = this.props;
        const {selectedPackages} = this.state;

        if (selectedPackages.length === 0) {
            NotificationService.showNotification({
                severity: 'warn',
                summary: 'Selection Required',
                detail: 'Please select at least one package'
            });
            return;
        }

        if (!this.state.selectedClinicId) {
            NotificationService.showNotification({
                severity: 'warn',
                summary: 'Selection Required',
                detail: 'Please select the clinic where the package is being sold'
            });
            return;
        }

        const selectedPackageIds = selectedPackages.map(pkg => pkg.id);
        const requestBody = {
            availablePackageIds: selectedPackageIds,
            clinicId: this.state.selectedClinicId
        };

        RemotingService.postRemoteCall(`api/patients/${patientId}/packages`, requestBody, (response) => {
            this.props.retrievePatientPackages();
            NotificationService.showNotification({
                severity: 'success',
                summary: 'Success',
                detail: `${selectedPackages.length} package(s) added to patient successfully`
            });
            closeModal();
        });
    }

    render() {
        return (
            <Modal visible={this.props.visible}
                   header="Available Packages"
                   submitContent="Add Selected Packages"
                   contentStyle={{height: 600, overflowY: 'auto'}}
                   submitDisabled={this.state.selectedPackages.length === 0}
                   submitAction={() => this.handlePackageAdd()}
                   closeAction={this.props.closeModal}
            >
                <div className="container-fluid px-3 pt-3">
                    <div className="row mb-3 align-items-end">
                        <div className="col-4">
                            <strong className="d-block mb-1">Clinic</strong>
                            <Dropdown className="w-100"
                                      value={this.state.selectedClinicId}
                                      optionLabel={"label"} optionValue={"id"}
                                      options={this.state.clinics}
                                      onChange={e => this.setState({selectedClinicId: e.value})}
                                      placeholder="Select Clinic"/>
                        </div>
                        <div className="col-8 text-right">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={this.addNewPackageRow}
                                disabled={this.state.selectedPackageIdForEdit !== null}
                            >
                                Add New Package
                            </Button>
                        </div>
                    </div>

                    <DataTable
                        value={this.state.packages}
                        emptyMessage="No packages found"
                        className="p-datatable-striped"
                        selectionMode="multiple"
                        selection={this.state.selectedPackages}
                        onSelectionChange={e => {
                            let newList = [...this.state.selectedPackages];
                            let selectedRow = e.value[0];
                            if (newList.some(pkg => pkg.id === selectedRow.id)) {
                                newList = newList.filter(pkg => pkg.id !== selectedRow.id);
                            } else {
                                newList.push(selectedRow);
                            }
                            this.setState({selectedPackages: newList});
                        }}
                        dataKey="id"
                        rowHover={true}
                    >

                        <Column
                            header="Name"
                            body={(packageData) => this.textInputTemplate(packageData, 'name')}
                            style={{minWidth: '150px'}}
                        />

                        <Column
                            header="Description"
                            body={(packageData) => this.textInputTemplate(packageData, 'description')}
                            style={{minWidth: '200px'}}
                        />

                        <Column
                            header="Type"
                            body={(packageData) => this.typeTemplate(packageData)}
                            style={{minWidth: '150px'}}
                        />

                        <Column
                            header="Sessions"
                            body={(packageData) => this.integerInputTemplate(packageData, 'sessionCount')}
                            style={{minWidth: '100px'}}
                        />

                        <Column
                            header="First Session Price"
                            body={(packageData) => this.numberInputTemplate(packageData, 'firstSessionPrice')}
                            style={{minWidth: '150px'}}
                        />

                        <Column
                            header="Price Per Session"
                            body={(packageData) => this.numberInputTemplate(packageData, 'pricePerSession')}
                            style={{minWidth: '150px'}}
                        />

                        <Column
                            header="Amount"
                            body={(packageData) => this.amountTemplate(packageData)}
                            style={{minWidth: '100px'}}
                        />

                        <Column
                            header="Discount %"
                            body={(packageData) => this.numberInputTemplate(packageData, 'discountPercentage')}
                            style={{minWidth: '120px'}}
                        />

                        <Column
                            header="Tax %"
                            body={(packageData) => this.numberInputTemplate(packageData, 'taxPercentage')}
                            style={{ minWidth: '100px' }}
                        />

                        <Column
                            header="Tax Amount"
                            body={(packageData) => this.taxAmountTemplate(packageData)}
                            style={{ minWidth: '120px' }}
                        />

                        <Column
                            header="Validity (Days)"
                            body={(packageData) => this.integerInputTemplate(packageData, 'validityInDays')}
                            style={{minWidth: '130px'}}
                        />

                        <Column
                            header="Actions"
                            body={(packageData) => this.actionTemplate(packageData)}
                            style={{minWidth: '120px'}}
                        />
                    </DataTable>
                </div>
            </Modal>
        );
    }
}

const mapStateToProps = state => {
    return {
        user: state.authentication.user
    };
};

export default connect(mapStateToProps)(AddPackagePanel);
